import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'
import { getResponseColor } from '@/utils/colors'

// Map question IDs to full Tableau labels (verified against topline report D11, page 6)
const IDENTITY_LABELS: Record<string, string> = {
  'Identity_Canadian': 'Canadian',
  'Identity_NewCanadian': 'New Canadian or Immigrant',
  'Identity_TempWorker': 'Temporary foreign worker or student',
  'Identity_Indigenous': 'Indigenous or First Nations',
  'Identity_Settler': 'Settler',
  'Identity_Traditional': 'Traditional',
  'Identity_NatureLover': 'Nature-lover',
  'Identity_Hunter': 'Hunter',
  'Identity_Angler': 'Angler',
  'Identity_Wellness': 'Wellness lifestyle',
}

// Question order: sorted by "Very much" percentage (highest to lowest in totals)
const IDENTITY_QUESTION_ORDER = [
  'Identity_Canadian',      // 81.9%
  'Identity_NatureLover',   // 52.6%
  'Identity_Traditional',   // 30.7%
  'Identity_Wellness',      // 25.3%
  'Identity_Settler',       // 12.2%
  'Identity_NewCanadian',   // 9.1%
  'Identity_Indigenous',    // 8.9%
  'Identity_Angler',        // 8.9%
  'Identity_Hunter',        // 8.6%
  'Identity_TempWorker',    // 6.0%
]

// Response order: most to least identification
const IDENTITY_RESPONSE_ORDER = ['Very much', 'Somewhat', 'No']

export function Identity() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  // Get identity questions in sorted order (by "Very much" percentage)
  const identityQuestions = data
    ? IDENTITY_QUESTION_ORDER
        .filter(id => data.questions[id])
        .map(id => [id, data.questions[id]] as [string, typeof data.questions[string]])
    : []

  // Sort responses helper
  const sortResponses = (responses: Array<{ label: string; percentage: number }>) => {
    return [...responses].sort((a, b) => {
      const aIdx = IDENTITY_RESPONSE_ORDER.indexOf(a.label)
      const bIdx = IDENTITY_RESPONSE_ORDER.indexOf(b.label)
      if (aIdx === -1) return 1
      if (bIdx === -1) return -1
      return aIdx - bIdx
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Identity
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Break: {breakName}
        </span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          Please say how well each of the following terms describes you.
        </p>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {IDENTITY_RESPONSE_ORDER.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getResponseColor('importance', label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading identity questions...</p>
          ) : isBreakActive ? (
            // Break is active - show multiple rows per question
            identityQuestions.map(([questionId, question]) => {
              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey('total', selectedBreak, value, selectedProvince)
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  questionData = question.data[buildFilterKey('total', selectedBreak, value)]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              const questionLabel = IDENTITY_LABELS[questionId] ||
                question.shortText ||
                questionId.replace('Identity_', '').replace(/_/g, ' ')

              return (
                <div key={questionId} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{questionLabel}</h3>
                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div className="w-40 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0 leading-tight" title={getDisplayName(row.value)}>
                          {getDisplayName(row.value)}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortResponses(row.data!.responses)}
                            scaleType="importance"
                            height={24}
                            showLabels={false}
                            normalize={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          ) : (
            // Total (no break) - show single bar per question with row label
            identityQuestions.map(([questionId, question]) => {
              const filterKey = buildFilterKey('total', 'total')
              const questionData = question.data[filterKey]

              if (!questionData) return null

              const questionLabel = IDENTITY_LABELS[questionId] ||
                question.shortText ||
                questionId.replace('Identity_', '').replace(/_/g, ' ')

              return (
                <div key={questionId} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{questionLabel}</h3>
                  <div className="flex items-center gap-2">
                    <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0">
                      National Sample
                    </div>
                    <div className="flex-1">
                      <HorizontalStackedBar
                        segments={sortResponses(questionData.responses)}
                        scaleType="importance"
                        height={28}
                        showLabels={false}
                        normalize={true}
                      />
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
